import { sdkApi } from '../api';
import { SearchArgType, SearchResults } from 'types/api';
import { SearchTypes } from 'models/enums';
import TCClient from 'client';
import type { Persona, UserData, UserSDK, UserInfo } from 'admin/types';

export type UserResult = {
  entity: UserSDK;
  entityId: string;
};

const getUserAccountSettings = async (userId: string, organizationId: string, user: UserInfo) => {
  const userAccountSettings = (await TCClient.adminUsers.getAccountSettings({
    userId,
    organizationId,
  })) as UserData;
  userAccountSettings.displayName = userAccountSettings.displayName?.length
    ? userAccountSettings.displayName
    : user?.displayName || '';
  userAccountSettings.location = user?.location || '';
  userAccountSettings.specialty = user?.specialty || '';
  userAccountSettings.insurance = user?.insurance || '';
  if (userAccountSettings.organizationSettings) {
    const orgIndex = userAccountSettings.organizationSettings.findIndex(
      (org) => org.organizationId === organizationId
    );
    if (orgIndex !== -1) {
      userAccountSettings.organizationSettings[orgIndex].title = user?.title || '';
      userAccountSettings.organizationSettings[orgIndex].department = user?.department || '';
    }
    const enhancedOrgSettings = await Promise.all(
      userAccountSettings.organizationSettings.map(async (orgSetting) => {
        if (orgSetting.permission_groups) {
          const permissionGroups = Array.isArray(orgSetting.permission_groups)
            ? orgSetting.permission_groups
            : [orgSetting.permission_groups];

          const permissionGroupsWithDetails = await Promise.all(
            permissionGroups.map(async (group) => {
              try {
                const personaDetails = (await TCClient.persona.findPersona({
                  personaId: group.persona_id,
                  organizationId: orgSetting.organizationId,
                })) as unknown as Persona;

                return {
                  ...group,
                  details: personaDetails,
                };
              } catch (error) {
                console.warn(`Failed to fetch persona details for ${group.persona_id}:`, error);
                return group;
              }
            })
          );

          return {
            ...orgSetting,
            permission_groups: permissionGroupsWithDetails,
          };
        }
        return orgSetting;
      })
    );

    userAccountSettings.organizationSettings =
      enhancedOrgSettings as UserData['organizationSettings'];
  }
  return userAccountSettings;
};

const adminUsersApi = sdkApi.injectEndpoints({
  endpoints: (build) => ({
    getUserAccountData: build.query<
      UserData,
      { userId: string; organizationId: string; userInfo: UserInfo; isLocationsEnabled: boolean }
    >({
      queryFn: async ({ userId, organizationId, userInfo, isLocationsEnabled }, _queryApi) => {
        try {
          const user = await TCClient.users.getUserData(userId, organizationId);
          const userAccountSettings = await getUserAccountSettings(userId, organizationId, user);
          const locations = isLocationsEnabled
            ? await TCClient.location.getUserLocations({
                accountId: userId,
                orgId: organizationId,
              })
            : undefined;
          return { data: { ...userAccountSettings, locations } };
        } catch (error) {
          console.error('error', error);
          const userAccountSettings = await getUserAccountSettings(
            userId,
            organizationId,
            userInfo
          );
          return { data: userAccountSettings };
        }
      },
    }),
    getUsers: build.query<SearchResults<UserResult>, SearchArgType>({
      queryFn: async ({ searchValue, orgId, continuation }, _queryApi) => {
        const res = await TCClient.search.query<UserResult>({
          version: 'SEARCH_PARITY',
          query: { name: '' },
          organizationId: orgId,
          types: [SearchTypes.USER],
          sort: ['firstName'],
          isNewAdminSearch: true,
          bool: {
            must: {
              any: searchValue,
              display_name: searchValue,
              first_name: searchValue,
              last_name: searchValue,
              title: searchValue,
            },
            must_not: { feature_service: 'role' },
          },
          continuation: continuation || '',
        });
        return { data: res };
      },
      serializeQueryArgs: ({ queryArgs }) => {
        const { searchValue, orgId } = queryArgs;
        return { searchValue, orgId };
      },
      merge: (currentCache, newItems) => {
        if (newItems.metadata.firstHit === 1) {
          currentCache.results = newItems.results;
          currentCache.metadata = newItems.metadata;
        } else {
          currentCache.results.push(...newItems.results);
          currentCache.metadata = newItems.metadata;
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return true;
      },
    }),
    removeUserFromOrg: build.mutation<
      string[],
      { userIds: string[]; orgId: string; inputValue: string; useScimDeletion?: boolean }
    >({
      queryFn: async (
        { userIds, orgId, useScimDeletion },
        _queryApi,
        _extraOptions,
        _baseQuery
      ) => {
        const deleteUserFn = useScimDeletion
          ? async (userId: string): Promise<{ type: string }> =>
              (await TCClient.scim.deleteScimUser(orgId, userId)) as { type: string }
          : async (userId: string): Promise<{ type: string }> =>
              (await TCClient.adminUsers.organizationLeave({
                orgId,
                userId,
              })) as Promise<{ type: string }>;

        const values = await Promise.allSettled(userIds.map((userId) => deleteUserFn(userId)));
        const finalIds = values
          .map((res, idx) => {
            if (res.status === 'rejected' || res.value.type === 'error') return '';
            return userIds[idx];
          })
          .filter((id) => id);
        return { data: finalIds };
      },
      async onQueryStarted({ orgId, inputValue }, { dispatch, queryFulfilled }) {
        try {
          const { data: userIds } = await queryFulfilled;
          if (userIds.length === 0) return;
          dispatch(
            adminUsersApi.util.updateQueryData(
              'getUsers',
              { orgId, searchValue: inputValue },
              (usersCache) => {
                const { results } = usersCache;
                Object.assign(usersCache, {
                  results: [...results.filter((result) => !userIds.includes(result.entityId))],
                });
              }
            )
          );
        } catch {}
      },
    }),
  }),
  overrideExisting: true,
});

export const { useGetUsersQuery, useRemoveUserFromOrgMutation, useGetUserAccountDataQuery } =
  adminUsersApi;
